import { z } from 'zod';
import { I18nText } from '../i18n';

/** Custom sign-up field beyond the built-ins (name, phone, gender, age/birthYear, handicap). */
export const CustomFieldSchema = z.object({
  key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/),
  label: I18nText,
  type: z.enum(['TEXT', 'NUMBER', 'SELECT', 'CHECKBOX']),
  required: z.boolean().default(false),
  options: z.array(z.object({ value: z.string(), label: I18nText })).optional(),
});
export type CustomField = z.infer<typeof CustomFieldSchema>;

/** Tournament.registration */
export const RegistrationSettingsSchema = z.object({
  enabled: z.boolean().default(true),
  opensAt: z.iso.datetime({ offset: true }).nullable().default(null),
  closesAt: z.iso.datetime({ offset: true }).nullable().default(null),
  capacity: z.int().min(1).nullable().default(null),
  /** true: submissions are PENDING until an admin approves; false: auto-approve until capacity */
  requiresApproval: z.boolean().default(true),
  /** when capacity is reached new sign-ups go to WAITLISTED (else rejected) */
  waitlist: z.boolean().default(true),
  /** built-in field requirements */
  fields: z
    .object({
      phone: z.enum(['REQUIRED', 'OPTIONAL', 'HIDDEN']).default('REQUIRED'),
      email: z.enum(['REQUIRED', 'OPTIONAL', 'HIDDEN']).default('OPTIONAL'),
      gender: z.enum(['REQUIRED', 'OPTIONAL', 'HIDDEN']).default('REQUIRED'),
      birthYear: z.enum(['REQUIRED', 'OPTIONAL', 'HIDDEN']).default('OPTIONAL'),
      /** 나이 (age at tournament start). AGE derivation uses age if given, else startDate year - birthYear */
      age: z.enum(['REQUIRED', 'OPTIONAL', 'HIDDEN']).default('HIDDEN'),
      handicap: z.enum(['REQUIRED', 'OPTIONAL', 'HIDDEN']).default('OPTIONAL'),
      nationality: z.enum(['REQUIRED', 'OPTIONAL', 'HIDDEN']).default('OPTIONAL'),
    })
    .default({
      phone: 'REQUIRED',
      email: 'OPTIONAL',
      gender: 'REQUIRED',
      birthYear: 'OPTIONAL',
      age: 'HIDDEN',
      handicap: 'OPTIONAL',
      nationality: 'OPTIONAL',
    }),
  customFields: z.array(CustomFieldSchema).default([]),
  /** shown on the sign-up page */
  notice: I18nText.nullable().default(null),
});
export type RegistrationSettings = z.infer<typeof RegistrationSettingsSchema>;

/** Participant.registrationData */
export const RegistrationPayloadSchema = z.object({
  fullName: z.string().min(1).max(120),
  phone: z.string().max(40).nullable().optional(),
  email: z.email().nullable().optional(),
  gender: z.enum(['M', 'F']).nullable().optional(),
  birthYear: z.int().min(1900).max(2100).nullable().optional(),
  age: z.int().min(5).max(110).nullable().optional(),
  handicapIndex: z.number().min(-10).max(54).nullable().optional(),
  nationality: z.string().length(2).nullable().optional(),
  isPro: z.boolean().optional(),
  /** category key -> option value */
  categories: z.record(z.string(), z.string()).default({}),
  custom: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
    .default({}),
  locale: z.string().optional(),
  /**
   * tee the player picked (a tee of the tournament's layouts, Participant.teeBoxId); null =
   * automatic (tee rules). Omitted = the logged-in user's preferred tee when it matches, else
   * automatic.
   */
  teeBoxId: z.uuid().nullable().optional(),
});
export type RegistrationPayload = z.infer<typeof RegistrationPayloadSchema>;

/** TeeAssignmentRule.match */
export const TeeRuleMatchSchema = z.array(
  z.object({ key: z.string(), values: z.array(z.string()).min(1) }),
);
export type TeeRuleMatch = z.infer<typeof TeeRuleMatchSchema>;

/** SpecialContest.eligibility and generic category filters: key -> allowed values */
export const CategoryFilterSchema = z.record(z.string(), z.array(z.string()));
export type CategoryFilter = z.infer<typeof CategoryFilterSchema>;
